// Professional SVG Components for Printer UI

export const MaticaPrinterSVG = ({ className = "", size = 120 }: { className?: string; size?: number }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 200 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
    >
        {/* Base */}
        <rect x="30" y="140" width="140" height="30" rx="4" fill="url(#printerGradient)" />

        {/* Main Body */}
        <rect x="40" y="80" width="120" height="60" rx="6" fill="url(#bodyGradient)" />

        {/* Top Panel */}
        <rect x="45" y="70" width="110" height="15" rx="3" fill="url(#panelGradient)" />

        {/* Card Slot */}
        <rect x="60" y="100" width="80" height="25" rx="2" fill="#1e293b" opacity="0.8" />
        <rect x="65" y="105" width="70" height="15" rx="1" fill="#0f172a" />

        {/* LED Indicators */}
        <circle cx="70" cy="75" r="3" fill="#22c55e" opacity="0.9">
            <animate attributeName="opacity" values="0.9;0.3;0.9" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="85" cy="75" r="3" fill="#3b82f6" opacity="0.7" />

        {/* Brand Label */}
        <rect x="120" y="72" width="25" height="8" rx="1" fill="#475569" opacity="0.6" />

        {/* Ventilation Slots */}
        <line x1="50" y1="90" x2="70" y2="90" stroke="#334155" strokeWidth="1" opacity="0.4" />
        <line x1="50" y1="95" x2="70" y2="95" stroke="#334155" strokeWidth="1" opacity="0.4" />

        {/* Shadow */}
        <ellipse cx="100" cy="172" rx="60" ry="8" fill="#000" opacity="0.15" />

        <defs>
            <linearGradient id="printerGradient" x1="30" y1="140" x2="30" y2="170">
                <stop offset="0%" stopColor="#475569" />
                <stop offset="100%" stopColor="#334155" />
            </linearGradient>
            <linearGradient id="bodyGradient" x1="40" y1="80" x2="40" y2="140">
                <stop offset="0%" stopColor="#64748b" />
                <stop offset="100%" stopColor="#475569" />
            </linearGradient>
            <linearGradient id="panelGradient" x1="45" y1="70" x2="45" y2="85">
                <stop offset="0%" stopColor="#94a3b8" />
                <stop offset="100%" stopColor="#64748b" />
            </linearGradient>
        </defs>
    </svg>
);

export const StatusIconSVG = ({ type, className = "" }: { type: 'ready' | 'warning' | 'error' | 'printing'; className?: string }) => {
    const configs = {
        ready: { color: '#22c55e', path: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
        warning: { color: '#f59e0b', path: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' },
        error: { color: '#ef4444', path: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z' },
        printing: { color: '#3b82f6', path: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' }
    };

    const config = configs[type];

    return (
        <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={config.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d={config.path} />
        </svg>
    );
};

export const CommandIconSVG = ({ type, className = "" }: { type: 'feed' | 'eject' | 'reject' | 'reset'; className?: string }) => {
    const icons = {
        feed: (
            <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 14l-7 7m0 0l-7-7m7 7V3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        eject: (
            <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 10l7-7m0 0l7 7m-7-7v18" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        reject: (
            <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        reset: (
            <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        )
    };

    return icons[type];
};

export const RibbonIconSVG = ({ className = "" }: { className?: string }) => (
    <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="8" r="6" />
        <path d="M8.5 14L6 22l6-3 6 3-2.5-8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const CardIconSVG = ({ className = "" }: { className?: string }) => (
    <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <path d="M2 10h20" />
    </svg>
);
